/**
 * recenter-headline plugin
 *
 * For headlines built as "static text + a vertical word carousel" — e.g.
 * `v3-outfits-carousel.liquid`'s "Outfits for every" + [day | weekend | …].
 *
 * The problem it solves: the word carousel needs a fixed-width box (its slides
 * are absolutely laid out by Swiper and its wrapper clips), so the word's left
 * edge is pinned while its right edge floats with word length. The headline's
 * layout box is centered but its *ink* never is, and it re-balances on every
 * slide change.
 *
 * The fix: reserve a box as wide as the WIDEST word, center the whole row, then
 * translate the headline right by half the slack of the currently-visible word:
 *
 *     shift = (reserve - activeWordWidth) / 2
 *
 * which puts the union of "static text + current word" dead centre. The gap
 * between the static text and the word stays constant; the line as a whole
 * drifts a few px per word (the rotating-word headline look). Set
 * `data-recenter-headline-damping` below 1 to trade centring accuracy for less
 * visible drift.
 *
 * Why a transform and not an animated width: Swiper's base CSS puts
 * `overflow: hidden` on `.swiper`, so shrinking the box to the active word
 * would horizontally clip the incoming/outgoing word mid-transition. The box
 * here never shrinks — only a (GPU-cheap) transform moves.
 *
 * --- Markup contract ---
 *
 *   <h2 data-recenter-headline-root>            ← the element that gets shifted
 *     <span>Outfits for every</span>
 *     <span class="swiper-section"
 *           data-recenter-headline="true"       ← opt in
 *           data-recenter-headline-min-width="990">
 *       <div class="swiper"> … word slides … </div>
 *     </span>
 *   </h2>
 *
 * The plugin writes three custom properties; the section's stylesheet consumes
 * them (see `src/css/sections/section-card-carousel.scss`):
 *
 *   --headline-word-reserve  on the .swiper-section  → its width
 *   --headline-shift         on the root             → translateX()
 *   --headline-shift-speed   on the root             → transition-duration
 *
 * Below `data-recenter-headline-min-width` the shift is pinned to 0px, for
 * headlines that stack the static text above the word on mobile (where each
 * line centres itself and there's nothing to correct).
 */

/** Viewport width at or above which the shift applies. */
const DEFAULT_MIN_WIDTH = 990;

/** Fallback transition duration when the swiper reports no `speed`. */
const DEFAULT_SPEED = 300;

const stateBySwiper = new WeakMap();

/**
 * Width of the rendered *text* inside `slide`, in px.
 *
 * `slide.offsetWidth` is useless here: the word sits inside a full-width block
 * (richtext renders a `<p>`), so it would report the container width — the very
 * thing we're trying to compute — and the measurement would chase its own tail.
 *
 * A Range whose endpoints are both inside text nodes yields the text quads
 * instead of element border boxes, so this measures ink. Endpoints are trimmed
 * to non-whitespace so Liquid's indentation can't pad the result. Requires the
 * slide text to be `white-space: nowrap` (it is, for this headline treatment),
 * which makes the measurement independent of the container width and therefore
 * safe to take before or after the reserve is applied.
 *
 * Returns 0 for a slide with no text.
 */
export function measureInkWidth(slide) {
  if (!slide) return 0;

  const walker = document.createTreeWalker(slide, NodeFilter.SHOW_TEXT);
  let first = null;
  let last = null;
  let node;
  while ((node = walker.nextNode())) {
    if (!node.nodeValue || !node.nodeValue.trim()) continue;
    if (!first) first = node;
    last = node;
  }
  if (!first) return 0;

  const range = document.createRange();
  range.setStart(first, first.nodeValue.length - first.nodeValue.trimStart().length);
  range.setEnd(last, last.nodeValue.trimEnd().length);
  return range.getBoundingClientRect().width;
}

export const recenterHeadline = {
  name: 'recenter-headline',

  matches(_el, section) {
    return section?.dataset?.recenterHeadline === 'true';
  },

  afterInit(swiper, ctx) {
    const section = ctx.parentSwiperSection;
    const root = section.closest('[data-recenter-headline-root]');
    if (!root) {
      console.warn(
        '[recenter-headline] no ancestor carries `data-recenter-headline-root`; ' +
          'nothing to shift. Add it to the headline element that wraps both the ' +
          'static text and this word carousel.',
        section
      );
      return;
    }

    const parsedMin = parseInt(section.dataset.recenterHeadlineMinWidth, 10);
    const parsedDamping = parseFloat(section.dataset.recenterHeadlineDamping);

    const state = {
      root,
      section,
      minWidth: Number.isNaN(parsedMin) ? DEFAULT_MIN_WIDTH : parsedMin,
      damping: Number.isNaN(parsedDamping) ? 1 : parsedDamping,
      widths: [],
      reserve: 0,
      unsubscribeResize: null,
    };
    stateBySwiper.set(swiper, state);

    // Match the shift to the word swap so they read as one movement.
    root.style.setProperty(
      '--headline-shift-speed',
      `${swiper.params?.speed ?? DEFAULT_SPEED}ms`
    );

    const measure = () => {
      state.widths = (swiper.slides || []).map(measureInkWidth);
      state.reserve = state.widths.length ? Math.max(...state.widths) : 0;
    };

    const apply = () => {
      if (!state.reserve) return;
      // Ceil so sub-pixel rounding can't clip the widest word's last glyph.
      section.style.setProperty(
        '--headline-word-reserve',
        `${Math.ceil(state.reserve)}px`
      );

      if (window.innerWidth < state.minWidth) {
        root.style.setProperty('--headline-shift', '0px');
        return;
      }

      // Index into `widths` with activeIndex, not realIndex: `widths` is
      // aligned to `swiper.slides`, which includes loop duplicates.
      const active = state.widths[swiper.activeIndex] ?? state.reserve;
      const shift = ((state.reserve - active) / 2) * state.damping;
      root.style.setProperty('--headline-shift', `${shift.toFixed(2)}px`);
    };

    const remeasureAndApply = () => {
      if (swiper.destroyed) return;
      measure();
      apply();
    };

    swiper.on('slideChange', apply);

    remeasureAndApply();

    // Re-measure once webfonts land. This headline is set in a display face;
    // measuring against the fallback puts the first paint visibly off-centre.
    document.fonts?.ready
      ?.then(remeasureAndApply)
      .catch(() => {
        /* font loading failed — keep the fallback-font measurement */
      });

    // Shared resize bus, not our own listener — see "Don't attach your own
    // window.resize listener from a plugin" in library/swiper/Swiper-Architecture.md.
    // Font size is breakpoint-dependent here, so widths must be re-measured,
    // not just re-applied.
    state.unsubscribeResize = ctx.initializer.onResize(remeasureAndApply);
  },

  onDestroy(swiper) {
    const state = stateBySwiper.get(swiper);
    if (!state) return;

    state.unsubscribeResize?.();
    state.root.style.removeProperty('--headline-shift');
    state.root.style.removeProperty('--headline-shift-speed');
    state.section.style.removeProperty('--headline-word-reserve');

    stateBySwiper.delete(swiper);
  },
};

export default recenterHeadline;
